import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  ApplicationDoc,
  ApplicationsDict,
  EMPTY_VISA_APPLICATION,
  VisaApplication,
} from "../../types/visa-application";

import { v4 as uuidv4 } from "uuid";

// import { functions } from "../../types/firebase";
import { httpsCallable } from "firebase/functions";

import {
  Box,
  Button,
  Chip,
  MenuItem,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from "@mui/material";
import { TableCell } from "@mui/material";
import { Tooltip, IconButton } from "@mui/material";
import {
  Save as SaveIcon,
  EditNote as EditNoteIcon,
  SportsScoreOutlined as FinishFlag,
  VerifiedUserOutlined as PrivacyIcon,
  CheckCircleOutlineOutlined as CheckIconOutlined,
  Check as CheckIcon,
  Slideshow,
  PresentToAll,
  CancelPresentation,
  InfoOutlined as InfoIcon,
  Badge,
} from "@mui/icons-material";

import { Applicant, ApplicantsDict } from "../../types/applicants";
import { SponsorsDict } from "../../types/sponsors";
import { SeasonsDict, WorkOrdersDict } from "../../types/orders";
import SimpleLinkDialog from "../dialogs/dialogSimpleLink";
import ApplicationDialog from "../dialogs/dialogApplicationSettings";
import { BASE_URL, copyToClipboard, isDateInThePast } from "../../utils/utils";
import { applicantFullName } from "../../utils/applicant-utils";
import ApplicationStatusDialog from "../dialogs/dialogApplicationStatus";
import { TitledPage } from "../../components/titled-page";
import { HeadCells, Order, getComparator } from "../../types/tables";
import { EnhancedTableHeadContents } from "../../components/enhanced-table";
import { loadSponsors } from "../../data-functions/system-data_api";
import { AuthContext } from "../../components/auth-provider";
import { loadSeasons, loadWorkOrders } from "../../data-functions/orders-api";
import {
  deleteApplications,
  loadApplication,
  loadApplications,
  storeApplication,
  updateApplication,
} from "../../data-functions/applications-api";
import { addHOPSCosApplicants } from "../../utils/exporters/exporters";
import { loadAgents } from "../../data-functions/agent-api";
import { AgentsDict } from "../../types/agents";
import { TableVirtuoso } from "react-virtuoso";
import {
  generateApplicantEmailExtensionCode,
  loadApplicant,
  loadApplicants,
} from "../../data-functions/applicants-api";
import { VirtuosoTableComponents } from "../../components/enhanced-virtuoso-table";
import ApplicationsBatchUpdateDialog, {
  BatchUpdateProps,
} from "../dialogs/dialogApplicationBatchUpdate";
import {
  displayStatusDescription,
  displayStatusLabel,
} from "../../utils/application-utils";
import { functions } from "../../types/firebase";
import {
  VirtualPresentationInstance,
  VirtualPresentationsDict,
} from "../../types/virtual-presentations";
import ApplicationVirtualPresentationDialog from "../dialogs/dialogApplicationVirtualPresentation";
import _, { forEach } from "lodash";
import { loadVirtualPresentations } from "../../data-functions/virtual_presentataions_api";
import ApplicationRow from "../../components/applications/application-row";
import { LogEntry } from "../../types/commons";
import { logEvent } from "../../utils/db-utils";
import { DocumentHistoryDialog } from "../dialogs/dialogDocumentHistory";
import { hasRole } from "../../utils/user-utils";
import { loadCampaigsByApplicantEmail } from "../../data-functions/recruiting_api";
import { ApplicationDetails } from "../../components/applications/application-details";
import WorkOrderSearchBox from "../../components/applications/WorkOrderSearchBox";
import { syncApplicantDocumentsFromRecruiting } from "../../data-functions/applicant-documents-api";

interface MailDataFormResult {
  data: {
    status: string;
    message: string;
    // Define other properties here if needed
  };
}

// Add this type for batch update progress statistics
type BatchUpdateStats = {
  total: number;
  current: number;
  updated: number;
  failed: number;
};

// Add type for batch update stage
type BatchUpdateStage = "idle" | "updating" | "fetching" | "complete";

type PassportValidityWarning = {
  applicationId: string;
  applicantName: string;
  workOrderName: string;
  workOrderEndDate: string;
  isAssumedWorkOrderEndDate: boolean;
  passportExpiryDate: string;
  requiredValidUntil: string;
};

const parseLocalDate = (dateString: string | undefined): Date | null => {
  if (!dateString) {
    return null;
  }

  const date = new Date(`${dateString}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date.getTime());
  const day = result.getDate();
  result.setMonth(result.getMonth() + months);

  if (result.getDate() < day) {
    result.setDate(0);
  }

  return result;
};

const formatDateForDisplay = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getPassportValidityWarningText = (
  warning: PassportValidityWarning,
): string => {
  const workOrderEndDateText = warning.isAssumedWorkOrderEndDate
    ? `${warning.workOrderName}'s assumed end date ${warning.workOrderEndDate}`
    : `${warning.workOrderName} ends on ${warning.workOrderEndDate}`;

  return `${warning.applicantName}'s passport expires on ${warning.passportExpiryDate}, less than 6 months after ${workOrderEndDateText}. Passport should be valid until at least ${warning.requiredValidUntil}.`;
};

const headCells: HeadCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
    sortable: true,
  },
  {
    id: "workOrderId",
    numeric: false,
    disablePadding: false,
    label: "Work Order",
    sortable: true,
  },
  {
    id: "finished",
    numeric: false,
    disablePadding: false,
    label: "",
    tooltip: "Data form expiry date",
    sortable: true,
    icon: <FinishFlag />,
    width: "100px",
  },
  {
    id: "privacy_policy_accepted",
    numeric: false,
    disablePadding: false,
    label: "",
    tooltip: "Privacy policy accepted",
    sortable: true,
    icon: <PrivacyIcon />,
    width: "30px",
  },
  {
    id: "virtual_presentation",
    numeric: false,
    disablePadding: false,
    label: "",
    tooltip: "Virtual presentation status",
    sortable: true,
    icon: <Slideshow />,
    width: "30px",
  },
  {
    id: "has_driving_license",
    numeric: false,
    disablePadding: false,
    label: "",
    tooltip: "Driving License",
    sortable: true,
    icon: <Badge />,
    width: "30px",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Status",
    sortable: true,
    width: "130px",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "",
    sortable: false,
    width: "180px",
  },
];

const ApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<ApplicationsDict>({});
  const [applicants, setApplicants] = useState<ApplicantsDict>({});
  const [seasons, setSeasons] = useState<SeasonsDict>({});
  const [sponsors, setSponsors] = useState<SponsorsDict>({});
  const [agents, setAgents] = useState<AgentsDict>({});
  const [orders, setOrders] = useState<WorkOrdersDict>({});
  const [presentations, setPresentations] = useState<VirtualPresentationsDict>(
    {},
  );
  const [selectedApplication, setSelectedApplication] =
    useState<ApplicationDoc | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<string>("");
  const [selectedSeason, setSelectedSeason] = useState<string>("");

  const [showOpenOrders, setShowOpenOrders] = useState<boolean>(true);

  const [open, setOpen] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [openVPDilog, setOpenVPDialog] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  //const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLButtonElement | null>(null);
  //const [statusApplicationId, setStatusApplicationId] = useState<string>("");

  //  const [editMode, setEditMode] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [currentLink, setCurrentLink] = useState("");
  const [searchValue, setSearchValue] = useState<string>("");

  const [openBatchUpdate, setOpenBatchUpdate] = useState(false);
  const [batchUpdateValues, setBatchUpdateValues] = useState<BatchUpdateProps>(
    {},
  );
  const [passportWarnings, setPassportWarnings] = useState<
    PassportValidityWarning[]
  >([]);

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("name");
  const [selected, setSelected] = useState<readonly string[]>([]);

  // State for batch update progress dialog
  const [batchUpdateStage, setBatchUpdateStage] =
    useState<BatchUpdateStage>("idle");
  const [progress, setProgress] = useState<BatchUpdateStats>({
    total: 0,
    current: 0,
    updated: 0,
    failed: 0,
  });

  const [editAppl, setEditAppl] = useState<ApplicationDoc>({
    id: "",
    application: { ...EMPTY_VISA_APPLICATION },
  });
  const [historyDoc, setHistoryDoc] = useState<string>("");
  const [generatingApplicantCodeId, setGeneratingApplicantCodeId] =
    useState<string>("");

  const { currentUser } = useContext(AuthContext)!;

  const [sortWorkOrdersBy, setSortWorkOrdersBy] = useState<"name" | "date">(
    "name",
  );

  const getPassportValidityWarning = (
    application: VisaApplication,
    applicantOverride?: Applicant,
    applicationId = "",
  ): PassportValidityWarning | null => {
    const workOrder = application.workOrderId
      ? orders[application.workOrderId]
      : undefined;
    const applicant =
      applicantOverride || applicants[application.applicantId || ""];
    const workOrderEndDate = workOrder?.end_date;
    const workOrderStartDate = workOrder?.start_date;
    const passportExpiryDate = applicant?.passport?.expiry_date;

    if (!workOrder || !passportExpiryDate || !applicant) {
      return null;
    }

    const savedEndDate = parseLocalDate(workOrderEndDate);
    const startDate = parseLocalDate(workOrderStartDate);
    const expiryDate = parseLocalDate(passportExpiryDate);

    if (!expiryDate || (!savedEndDate && !startDate)) {
      return null;
    }

    const endDate = savedEndDate || addMonths(startDate!, 6);
    const requiredValidUntil = addMonths(endDate, 6);

    if (expiryDate >= requiredValidUntil) {
      return null;
    }

    return {
      applicationId,
      applicantName: applicantFullName(applicant),
      workOrderName: workOrder.name,
      workOrderEndDate: formatDateForDisplay(endDate),
      isAssumedWorkOrderEndDate: !savedEndDate,
      passportExpiryDate,
      requiredValidUntil: formatDateForDisplay(requiredValidUntil),
    };
  };

  const getApplicationPassportValidityWarningText = (
    application: VisaApplication,
    applicationId = "",
  ) => {
    const warning = getPassportValidityWarning(
      application,
      undefined,
      applicationId,
    );

    return warning ? getPassportValidityWarningText(warning) : "";
  };

  const showPassportValidityWarnings = (
    warnings: (PassportValidityWarning | null)[],
  ) => {
    const visibleWarnings = warnings.filter(
      (warning): warning is PassportValidityWarning => !!warning,
    );

    if (visibleWarnings.length > 0) {
      setPassportWarnings(visibleWarnings);
    }
  };

  const fetchApplicants = async () => {
    console.log("loading applicants");
    let applicantsDict: ApplicantsDict = await loadApplicants(
      currentUser?.appUser,
    );
    console.log("applicants loaded");
    setApplicants(applicantsDict);
  };

  const handleCopyApplicantEmailExtensionCode = (code: string) => {
    copyToClipboard(code);
  };

  const handleGenerateApplicantCode = async (applicantId: string) => {
    if (!applicantId) {
      return;
    }

    setGeneratingApplicantCodeId(applicantId);
    try {
      const applicantDoc = await generateApplicantEmailExtensionCode(
        currentUser?.appUser,
        applicantId,
      );
      if (!applicantDoc) {
        return;
      }

      setApplicants((prevApplicants) => ({
        ...prevApplicants,
        [applicantDoc.id]: applicantDoc.applicant,
      }));
    } finally {
      setGeneratingApplicantCodeId("");
    }
  };

  const ensureApplicantCodeForVisaReady = async (
    application: VisaApplication,
  ) => {
    if (application.status !== "visa_ready" || !application.applicantId) {
      return;
    }

    const applicantDoc = await generateApplicantEmailExtensionCode(
      currentUser?.appUser,
      application.applicantId,
    );

    if (!applicantDoc) {
      return;
    }

    setApplicants((prevApplicants) => ({
      ...prevApplicants,
      [applicantDoc.id]: applicantDoc.applicant,
    }));
  };

  const fetchApplications = async (
    orderId: string = "all_orders",
    seasonId: string = "no_season",
  ) => {
    // load applications
    let filter: Record<string, string>;

    switch (orderId) {
      case "all_orders":
        filter = {};
        break;
      case "no_order":
        filter = { workOrderId: "" };
        break;

      default:
        filter = { workOrderId: orderId };
        break;
    }

    switch (seasonId) {
      case "no_season":
        filter = { ...filter, seasonId: "" };
        break;
      default:
        filter = { ...filter, seasonId };
        break;
    }

    console.log("filter: ", filter);

    const applicationsDict: ApplicationsDict = await loadApplications(
      currentUser?.appUser,
      filter,
    );

    setApplications(applicationsDict);
  };

  const fetchSponsors = async () => {
    const sponsorsDict = await loadSponsors(currentUser?.appUser);

    if (sponsorsDict) {
      setSponsors(sponsorsDict);
    } else {
      setSponsors({});
    }
  };

  const fetchAgents = async () => {
    const agentsDict = await loadAgents(currentUser?.appUser);

    if (agentsDict) {
      setAgents(agentsDict);
    } else {
      setAgents({});
    }
  };

  const fetchWorkOrders = async (seasonId: string) => {
    const newOrders = await loadWorkOrders(currentUser?.appUser);

    if (newOrders) {
      setOrders(newOrders);
      const fOrders = sortAndFilterOrders(newOrders, showOpenOrders, seasonId);

      if (fOrders.length > 0) {
        setSelectedOrder(fOrders[0][0]);
      }
    } else {
      setOrders({});
    }
  };

  const fetchPresentations = async () => {
    const presentationsDict: VirtualPresentationsDict =
      await loadVirtualPresentations(currentUser?.appUser);

    if (presentationsDict) {
      setPresentations(presentationsDict);
    } else {
      setPresentations({});
    }
  };

  const fetchSeasons = async () => {
    // loading Employers
    const seasonsDict = await loadSeasons(currentUser?.appUser);
    const year = new Date().getFullYear();
    let seasonId = Object.entries(seasonsDict).find(([id, season]) => {
      return new Date(season.start_date).getFullYear() === year;
    })?.[0];

    if (!seasonId) seasonId = "";

    setSeasons(seasonsDict);
    if (seasonsDict) setSelectedSeason(seasonId);

    fetchWorkOrders(seasonId);
  };

  useEffect(() => {
    console.log("start loading data");
    setShowOpenOrders(true);
    setSelectedApplication(null);
    setApplications({});
    fetchSponsors();
    fetchAgents();
    fetchSeasons();
    fetchPresentations();
    fetchApplicants();
    console.log("end loading data");
  }, [currentUser]);

  useEffect(() => {
    if (selectedOrder && selectedSeason) {
      setSearchValue("");
      fetchApplications(selectedOrder, selectedSeason);
    }
  }, [selectedOrder, selectedSeason]);

  useEffect(() => {
    const syncSelectedApplicantDocuments = async () => {
      const applicantId = selectedApplication?.application.applicantId;
      if (!applicantId || !applicants[applicantId]) {
        return;
      }

      const syncedApplicant = await syncApplicantDocumentsFromRecruiting(
        currentUser?.appUser,
        {
          id: applicantId,
          applicant: applicants[applicantId],
        },
        true,
      );

      if (!syncedApplicant.changed) {
        return;
      }

      setApplicants((currentApplicants) => ({
        ...currentApplicants,
        [applicantId]: syncedApplicant.applicantDoc.applicant,
      }));
    };

    void syncSelectedApplicantDocuments();
  }, [
    applicants[selectedApplication?.application.applicantId || ""],
    currentUser?.appUser,
    selectedApplication,
  ]);

  const createQuestionnaireLink = async (application: ApplicationDoc) => {
    if (!application.application.questionnaireId) {
      application.application.questionnaireId = uuidv4();
      await handleSaveApplication(application);
    }
  };

  const getWorkOrderStatusChip = (status: string | undefined) => {
    switch (status) {
      case "open":
        return <Chip label="Open" color="success" size="small" />;

      case "closed":
        return <Chip label="Closed" color="error" size="small" />;

      default:
        return (
          <Chip label="n/a" variant="outlined" color="primary" size="small" />
        );
    }
  };

  const handleAddNewApplication = async (
    applicationId: string,
    edit: boolean = false,
  ) => {
    let newApplication: ApplicationDoc;
    if (!edit) {
      newApplication = { id: "", application: { ...EMPTY_VISA_APPLICATION } };
      newApplication.application.workOrderId =
        selectedOrder &&
        selectedOrder !== "all_orders" &&
        selectedOrder !== "no_order"
          ? selectedOrder
          : "";
      newApplication.application.seasonId = selectedSeason;
      newApplication.application.sponsorId =
        currentUser?.appUser?.sponsorId || "";
      newApplication.application.agentId = currentUser?.appUser?.agentId || "";
      newApplication.application.questionnaireId = uuidv4();
    } else {
      newApplication = {
        id: applicationId,
        application: { ...applications[applicationId] },
      };
    }
    //    setEditMode(edit && !!selectedApplication);
    setEditAppl(newApplication);
    setOpen(true);
  };

  const handleClose = () => {
    if (open) {
      setOpen(false);
    }
  };

  // const handleOpenStatus = (event: React.MouseEvent<HTMLButtonElement>, applicationId: string) => {
  //   setStatusApplicationId(applicationId);
  //   setStatusAnchorEl(event.currentTarget);
  // };

  const handleCloseStatus = () => {
    // setStatusApplicationId("");
    // setStatusAnchorEl(null);

    if (openStatus) {
      setOpenStatus(false);
    }
  };

  const handleExport = async () => {
    const count = selected.length === 0 ? visibleRows.length : selected.length;
    if (
      !window.confirm(
        "Are you sure you want to export " + count + " applications?",
      )
    ) {
      return;
    }
    console.log("exporting " + count + " applications");
    const exportApplications: any[] = [];
    const exportIds: string[] = [];

    if (selected.length === 0) {
      for (const [id, application] of visibleRows) {
        let campaigns: string[] = [];
        if (
          application.applicantId &&
          applicants[application.applicantId] &&
          applicants[application.applicantId].email
        ) {
          campaigns = await loadCampaigsByApplicantEmail(
            applicants[application.applicantId].email || "",
          );
          console.log("loaded campaigns: ", campaigns);
        }

        exportApplications.push({
          application,
          applicant: applicants[application.applicantId] || null,
          workOrder: orders[application.workOrderId] || null,
          campaigns: campaigns,
        });
        exportIds.push(id);
      }
    } else {
      for (const id of selected) {
        const application = applications[id];
        let campaigns: string[] = [];
        if (
          application.applicantId &&
          applicants[application.applicantId] &&
          applicants[application.applicantId].email
        ) {
          campaigns = await loadCampaigsByApplicantEmail(
            applicants[application.applicantId].email || "",
          );
        }
        if (application) {
          exportApplications.push({
            application,
            applicant: applicants[application.applicantId] || null,
            workOrder: orders[application.workOrderId] || null,
            campaigns: campaigns,
          });
          exportIds.push(id);
        }
      }
    }

    try {
      // Convert applications data to CSV format:
      const csvData = addHOPSCosApplicants(exportApplications);
      const curentDate = new Date().toISOString().split("T")[0];
      // Create a blob from the CSV data and download:
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "applicants " + curentDate + ".csv"; // specify the file name
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } finally {
      const logEntry: LogEntry = {
        collection: "applications",
        documentId: exportIds.join(","),
        sponsorId: currentUser?.appUser?.sponsorId || "",
        operation: "export",
        changedFields: { applications: exportIds },
      };
      logEvent(logEntry);
    }
  };

  const handleBatchUpdate = async () => {
    if (selected.length === 0) {
      return;
    }

    let currentValues: BatchUpdateProps = { sendPresentationLink: false };
    selected.forEach((id) => {
      const application = applications[id];

      if (currentValues.agentId === undefined) {
        if (application.agentId) {
          currentValues.agentId = application.agentId;
        } else {
          currentValues.agentId = "";
        }
      } else if (application.agentId !== currentValues.agentId) {
        currentValues.agentId = "multiple";
      }

      if (currentValues.seasonId === undefined) {
        if (application.seasonId) {
          currentValues.seasonId = application.seasonId;
        } else {
          currentValues.seasonId = "";
        }
      } else if (application.seasonId !== currentValues.seasonId) {
        currentValues.seasonId = "multiple";
      }

      if (currentValues.workOrderId === undefined) {
        if (application.workOrderId) {
          currentValues.workOrderId = application.workOrderId;
        } else {
          currentValues.workOrderId = "";
        }
      } else if (application.workOrderId !== currentValues.workOrderId) {
        currentValues.workOrderId = "multiple";
      }

      if (currentValues.status === undefined) {
        if (application.status) {
          currentValues.status = application.status;
        } else {
          currentValues.status = "";
        }
      } else if (application.status !== currentValues.status) {
        currentValues.status = "multiple";
      }

      if (currentValues.dataForm_expiry_date === undefined) {
        if (application.dataForm_expiry_date) {
          currentValues.dataForm_expiry_date = application.dataForm_expiry_date;
        } else {
          currentValues.dataForm_expiry_date = "";
        }
      } else if (
        application.dataForm_expiry_date !== currentValues.dataForm_expiry_date
      ) {
        currentValues.dataForm_expiry_date = "multiple";
      }

      console.log("virtual presentation: ", application.virtualPresentation);
      if (currentValues.virtualPresentationId === undefined) {
        if (application.virtualPresentation?.virtualPresentationId) {
          currentValues.virtualPresentationId =
            application.virtualPresentation.virtualPresentationId;
          currentValues.expiry_date =
            application.virtualPresentation.expiry_date;
        } else {
          // setting it to "" shows that at least one application has been checked
          currentValues.virtualPresentationId = "";
        }
      } else if (
        (application.virtualPresentation?.virtualPresentationId || "") !==
        currentValues.virtualPresentationId
      ) {
        currentValues.virtualPresentationId = "multiple";
        currentValues.expiry_date = "";
      }
    });

    console.log("currentValues: ", currentValues);

    const initialValues: BatchUpdateProps = { ...currentValues };
    // if (currentValues.agentId !== "multiple") {
    //   initialValues.agentId = currentValues.agentId;
    // }

    // if (currentValues.seasonId !== "multiple") {
    //   initialValues.seasonId = currentValues.seasonId;
    // }

    // if (currentValues.workOrderId !== "multiple") {
    //   initialValues.workOrderId = currentValues.workOrderId;
    // }

    // if (currentValues.status !== "multiple") {
    //   initialValues.status = currentValues.status;
    // }

    console.log("initialValues: ", initialValues);

    setBatchUpdateValues(initialValues);
    setOpenBatchUpdate(true);
  };

  const onBatchUpdate = async (values: BatchUpdateProps) => {
    const newApplications: ApplicationsDict = { ...applications };

    let updatedApplications = 0;
    const totalSelected = selected.length;

    // Show progress dialog and set stage to updating
    setBatchUpdateStage("updating");
    setProgress({
      total: totalSelected,
      current: 0,
      updated: 0,
      failed: 0,
    });

    console.log("batch update values: ", values);

    let seasonMissmatch = false;
    if (values.seasonId && !values.workOrderId) {
      forEach(selected, (id) => {
        const woId = applications[id].workOrderId;
        if (woId && orders[woId].seasonId !== values.seasonId) {
          seasonMissmatch = true;
        }
      });

      if (seasonMissmatch) {
        const res = window.confirm(
          "The selected applications are assigned to work orders that do not macht the desired season. If you continue, the work orders will be changed too. Do you want to continue?",
        );
        if (!res) {
          return;
        }
      }
    }

    seasonMissmatch = false;
    if (
      !values.seasonId &&
      values.workOrderId &&
      values.workOrderId !== undefined
    ) {
      const woId = values.workOrderId;
      forEach(selected, (id) => {
        if (orders[woId].seasonId !== applications[id].seasonId) {
          seasonMissmatch = true;
        }
      });

      if (seasonMissmatch) {
        const res = window.confirm(
          "The selected applications are assigned to a seasons that do not macht the desired work order. If you continue, the applications will be assigned the correct season. Do you want to continue?",
        );
        if (!res) {
          return;
        }
      }
    }

    let vpMissmatch = false;
    if (values.virtualPresentationId) {
      forEach(selected, (id) => {
        if (applications[id].virtualPresentation?.virtualPresentationId) {
          vpMissmatch = true;
        }
      });

      if (vpMissmatch) {
        const res = window.confirm(
          "Some applications are already assigned a virtual presentation. If you continue, the corresponding applications will not be assigned the selected virtual presentation. Do you want to continue?",
        );
        if (!res) {
          return;
        }
      }
    }

    let vpMails: string[] = [];
    const passportWarningsAfterBatch: PassportValidityWarning[] = [];
    // let applChanges: Promise<ApplicationDoc | null>[] = [];

    // Use Promise.allSettled to track progress even if some updates fail
    for (const id of selected) {
      console.log(`checking application ${id} for updates`);
      const application = newApplications[id];
      const originalWorkOrderId = application.workOrderId || "";
      let changed = false;
      if (
        values.agentId !== undefined &&
        values.agentId !== application.agentId
      ) {
        application.agentId = values.agentId;
        console.log("setting agent");
        changed = true;
      }

      if (values.seasonId !== undefined && values.workOrderId !== undefined) {
        console.log("season and work order have changed");
        if (
          values.seasonId !== application.seasonId ||
          values.workOrderId !== application.workOrderId
        ) {
          application.seasonId = values.seasonId;
          application.workOrderId = values.workOrderId;
          console.log("setting work order && season");
          changed = true;
        }
      } else {
        console.log(
          "season only has changed: ",
          values.seasonId !== undefined &&
            values.seasonId !== application.seasonId,
        );
        if (
          values.seasonId !== undefined &&
          values.seasonId !== application.seasonId
        ) {
          const canSetSeason =
            !application.workOrderId ||
            (application.workOrderId &&
              ((orders[application.workOrderId] &&
                orders[application.workOrderId].seasonId === values.seasonId) ||
                !application.workOrderId));

          if (canSetSeason) {
            console.log("setting season");
            application.seasonId = values.seasonId;
            changed = true;
          }
        }

        if (
          values.workOrderId !== undefined &&
          values.workOrderId !== application.workOrderId
        ) {
          application.workOrderId = values.workOrderId;
          console.log("setting work order");
          const wo = orders[values.workOrderId];
          application.seasonId = wo.seasonId;
          changed = true;
        }
      }

      if (values.status !== undefined && values.status !== application.status) {
        application.status = values.status;
        console.log("setting status");
        changed = true;
      }

      if (
        values.dataForm_expiry_date !== undefined &&
        values.dataForm_expiry_date !== "multiple" &&
        values.dataForm_expiry_date !== application.dataForm_expiry_date
      ) {
        application.dataForm_expiry_date = values.dataForm_expiry_date;
        console.log("setting data form expiry date");
        changed = true;
      }

      if (
        values.virtualPresentationId !== undefined &&
        !application.virtualPresentation?.virtualPresentationId
      ) {
        if (values.virtualPresentationId !== "") {
          console.log("setting virtual presentation");
          application.virtualPresentation = {
            virtualPresentationId: values.virtualPresentationId,
            status: "new",
            link_id: uuidv4(),
            expiry_date: values.expiry_date,
          };
          if (values.sendPresentationLink) {
            vpMails.push(id);
          }
        } else {
          console.log("deleting virtual presentation");
          application.virtualPresentation = undefined;
        }

        changed = true;
      }

      const wasPlacedInWorkOrder =
        !!application.workOrderId &&
        originalWorkOrderId !== application.workOrderId;

      if (changed) {
        try {
          console.log("storing application: ", id);
          await storeApplication(
            currentUser?.appUser,
            { id, application },
            false,
          );
          await ensureApplicantCodeForVisaReady(application);
          newApplications[id] = application; // Keep local copy updated
          if (wasPlacedInWorkOrder) {
            const warning = getPassportValidityWarning(
              application,
              undefined,
              id,
            );

            if (warning) {
              passportWarningsAfterBatch.push(warning);
            }
          }
          updatedApplications++;
          setProgress((prev) => ({ ...prev, updated: prev.updated + 1 }));
        } catch (error) {
          console.error(`Failed to update application ${id}:`, error);
          // Increment failed counter on error
          setProgress((prev) => ({ ...prev, failed: prev.failed + 1 }));
          // Optionally handle individual update errors here
        }
      }
      // Update progress after processing each application
      setProgress((prev) => ({ ...prev, current: prev.current + 1 }));
    }

    if (
      values.virtualPresentationId &&
      values.sendPresentationLink &&
      vpMails.length > 0
    ) {
      await doSendInvitations(vpMails, "virtual_presentations");
    }

    // Set stage to fetching before refetching data
    setBatchUpdateStage("fetching");

    // Fetch updated applications data after all updates are processed
    await fetchApplications(selectedOrder, selectedSeason);

    // Mark update as complete for the dialog
    setBatchUpdateStage("complete");
    showPassportValidityWarnings(passportWarningsAfterBatch);

    // Keep the dialog open until closed manually
    // window.alert("Updated " + updatedApplications + " applications");
    console.log("updatedApplications: ", updatedApplications);
  };

  const doSendInvitations = async (appl: string[], template: string) => {
    if (appl.length > 0) {
      const mailDataFormFunction = httpsCallable(functions, "api/mailDataForm");
      const data: any = {
        template: template,
        applications: appl,
      };

      if (!currentUser?.appUser?.sponsorId) {
        data.sponsorId = applications[appl[0]].sponsorId;
      }

      try {
        const result = (await mailDataFormFunction(data)) as MailDataFormResult;
        console.log("result: ");
        console.log(result);
        if (result.data.status === "OK") {
          alert("Emails sent successfully");
        } else {
          alert("Error sending emails: " + result.data.message);
        }
      } catch (error) {
        console.error("Error calling cloud function:", error);
      }
    }
  };

  const handleSendInvitations = async (template: string) => {
    const appl: string[] = [];
    selected.forEach((id) => {
      appl.push(id);
      if (template === "data_form") {
        createQuestionnaireLink({ id, application: applications[id] });
      }
    });

    doSendInvitations(appl, template);
  };

  const handleSendSelectedInvition = async (
    applicationId: string,
    template: string,
  ) => {
    if (!applicationId) {
      console.log("no application selected");
      return;
    }
    const appl: string[] = [];
    appl.push(applicationId);
    doSendInvitations(appl, template);
  };

  const handleSendLink = async (applicationId: string) => {
    if (!applicationId) return;

    const application = applications[applicationId];
    createQuestionnaireLink({ id: applicationId, application });
    const baseUrl = BASE_URL + "/questionnaire/";
    setCurrentLink(baseUrl + application.questionnaireId);
    setOpenAlert(true);
  };

  const handleAlertClose = () => {
    //    setEditMode(false);
    setOpenAlert(false);
  };

  const handleAssignVirtualPresentation = async (applicationId: string) => {
    const newApplication = {
      id: applicationId,
      application: { ...applications[applicationId] },
    };
    setEditAppl(newApplication);
    setOpenVPDialog(true);
  };

  const handleCloseVPDialog = () => {
    setOpenVPDialog(false);
  };

  const handleToggleShowopenOrders = () => {
    console.log("toggle show opened campaigns");
    const newSO = !showOpenOrders;
    if (newSO && selectedOrder && orders[selectedOrder]) {
      if (orders[selectedOrder].status === "closed") {
        const fOrders = sortAndFilterOrders(orders, newSO, selectedSeason);
        if (fOrders.length > 0) {
          setSelectedOrder(fOrders[0][0]);
        } else setSelectedOrder("all_orders");
        setSelectedApplication(null);
      }
    }
    setShowOpenOrders(newSO);
  };

  const onInputComboChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedOrder(value);
    setSelectedApplication(null);
    if (value !== "all_orders") setSelected([]);
  };

  const onSeasonInputComboChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    console.log("season changed", event.target.value);
    const value = event.target.value;
    setSelectedSeason(value);
    const fOrders = sortAndFilterOrders(orders, showOpenOrders, value);
    if (fOrders.length > 0) {
      setSelectedOrder(fOrders[0][0]);
    } else setSelectedOrder("all_orders");
    setSelectedApplication(null);
    setSelected([]);
  };

  const handleSelectApplication = async (applicationId: string) => {
    if (applicationId) {
      // const docRef = doc(db, "applications", applicationId);
      // const applicationSnapshot = await getDoc(docRef);
      // if (applicationSnapshot) {
      //   loadedApplication = {
      //     id: applicationSnapshot.id,
      //     application: getApplicationFromFB(applicationSnapshot.data() as VisaApplicationFB),
      //   };
      // }
      console.log("selecting application: " + applicationId);
      const loadedApplication = {
        id: applicationId,
        application: { ...applications[applicationId] },
      };
      setSelectedApplication(loadedApplication);
    }
  };

  const handleEditApplication = async (applicationId: string) => {
    handleAddNewApplication(applicationId, true);
  };

  const handleShowHistory = async (documentId: string) => {
    setHistoryDoc(documentId);
    setOpenHistory(true);
  };

  // const handleEditApplicationStatus = async (applicationId: string) => {
  //   if (!selectedApplication || applicationId !== selectedApplication?.id) {
  //     await handleSelectApplication(applicationId);
  //   }
  //   setEditAppl({ id: applicationId, application: { ...applications[applicationId] } });
  //   setOpenStatus(true);
  // };

  // const handleStatusChange = (status: string) => {
  //   console.log(status); // Here you can call the function to change the status
  //   // setStatusApplicationId("");
  //   // setStatusAnchorEl(null);
  //   setOpenStatus(false); // Close the menu after selection
  // };

  const handleStatusChange = async (applicationId: string, status: string) => {
    if (!applicationId) return;

    const newApplications = { ...applications };
    const application = newApplications[applicationId];
    application.status = status;
    await updateApplication(currentUser?.appUser, applicationId, {
      status: application.status,
    });
    await ensureApplicantCodeForVisaReady(application);
    let loadedApplication = await loadApplication(
      currentUser?.appUser,
      applicationId,
    );

    if (loadedApplication) {
      newApplications[applicationId] = loadedApplication.application;
    } else loadedApplication = { id: applicationId, application };

    if (selectedApplication && selectedApplication.id === applicationId) {
      setSelectedApplication(loadedApplication);
    }

    setApplications(newApplications);
  };

  const handleSaveApplication = async (applicationDoc: ApplicationDoc) => {
    console.log("storing application: " + applicationDoc.id);
    const previousWorkOrderId = applicationDoc.id
      ? applications[applicationDoc.id]?.workOrderId || ""
      : "";
    const newWorkOrderId = applicationDoc.application.workOrderId || "";
    const storedApplication = await storeApplication(
      currentUser?.appUser,
      applicationDoc,
      true,
    );
    await ensureApplicantCodeForVisaReady(applicationDoc.application);
    if (selectedApplication && selectedApplication.id === applicationDoc.id) {
      console.log("updating the selected application");
      setSelectedApplication({ ...applicationDoc });
    }

    let currentApplicants = applicants;
    //check if the applicant is loaded in the list
    if (!currentApplicants[applicationDoc.application.applicantId]) {
      console.log("applicant not loaded, loading...");
      const newApplicants = { ...currentApplicants };
      const applicant = await loadApplicant(
        applicationDoc.application.applicantId,
      );
      if (applicant?.applicant) {
        newApplicants[applicationDoc.application.applicantId] =
          applicant?.applicant;
        currentApplicants = newApplicants;
        setApplicants(newApplicants);
      } else {
        console.log(
          "Applicant not found: " + applicationDoc.application.applicantId,
        );
        alert("Applicant not found!");
        return;
      }
    }

    const newApplications = _.cloneDeep(applications);
    if (storedApplication) {
      newApplications[storedApplication.id] = _.cloneDeep(
        applicationDoc.application,
      );
      setApplications(newApplications);

      if (newWorkOrderId && previousWorkOrderId !== newWorkOrderId) {
        showPassportValidityWarnings([
          getPassportValidityWarning(
            applicationDoc.application,
            currentApplicants[applicationDoc.application.applicantId],
            storedApplication.id,
          ),
        ]);
      }
    }
  };

  const handleSaveApplicationVP = async (applicationDoc: ApplicationDoc) => {
    console.log("storing application: " + applicationDoc.id);
    const storedApplication = await storeApplication(
      currentUser?.appUser,
      applicationDoc,
      false,
    );

    if (selectedApplication && selectedApplication.id === applicationDoc.id) {
      console.log("updating the selected application");
      setSelectedApplication({ ...applicationDoc });
    }

    const newApplications = _.cloneDeep(applications);
    if (storedApplication) {
      newApplications[applicationDoc.id] = _.cloneDeep(
        applicationDoc.application,
      );
      setApplications(newApplications);
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = visibleRows.map(([id, application]) => id);
      setSelected(newSelected);
    } else setSelected([]);
  };

  const handleCheckRow = (
    event: React.ChangeEvent<HTMLInputElement>,
    applicationId: string,
  ) => {
    // setChecked(event.target.checked);
    const selectedIndex = selected.indexOf(applicationId);
    const newSelected: string[] = [...selected];

    if (selectedIndex === -1) {
      newSelected.push(applicationId);
    } else {
      newSelected.splice(selectedIndex, 1);
    }

    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.includes(id);

  // const handleCallFunction = async () => {
  //   //    const assignCosNumbers = httpsCallable(functions, "api/assignCosNumbers");
  //   // const result = await assignCosNumbers({ cos: cosNumbers });
  //   const appl = await loadApplications(currentUser?.appUser);
  //   const wOrders = await loadWorkOrders(currentUser?.appUser);
  //   let count = 0;
  //   Object.keys(appl).forEach((id: string) => {
  //     const application = appl[id] as VisaApplication;
  //     let changed = false;
  //     if (application.sponsorId === undefined) {
  //       console.log(id + " has no sponsor");
  //     }
  //     if (changed) {
  //       storeApplication(currentUser?.appUser, { id, application }, false);
  //       count++;
  //     }
  //   });
  //   alert("Changed " + count + " applications");
  // };

  const handleDelete = async (applicationId: string | null) => {
    if (!applicationId) return;

    if (window.confirm("Are you sure you want to delete this application?")) {
      await deleteApplications([applicationId]);
      //      await deleteDoc(doc(db, "applications", applicationId));
      const newApplications = { ...applications };
      delete newApplications[applicationId];
      if (selectedApplication && selectedApplication.id === applicationId) {
        setSelectedApplication(null);
      }
      if (selected.indexOf(applicationId) >= 0) {
        const newSelected = [...selected];
        newSelected.splice(selected.indexOf(applicationId), 1);
        setSelected(newSelected);
      }

      setApplications(newApplications);
    }
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getApplicantFullName = (applicantId: string | null | undefined) => {
    if (!applicantId) return "n/a";
    return applicantFullName(applicants[applicantId]);
  };

  const getOrderName = (workOrderId: string) => {
    return orders[workOrderId]?.name || "";
  };

  const displayVPStatusIcon = (
    vpInstance: VirtualPresentationInstance | null | undefined,
  ) => {
    if (!vpInstance || !vpInstance.virtualPresentationId) {
      return <></>;
    }

    if (
      vpInstance.expiry_date &&
      vpInstance.status !== "completed" &&
      isDateInThePast(vpInstance.expiry_date)
    ) {
      return (
        <Tooltip title="virtual presentation expired">
          <CancelPresentation sx={{ color: "red" }} />
        </Tooltip>
      );
    }

    switch (vpInstance.status) {
      case "new":
        return (
          <Tooltip title="virtual presentation assigned">
            <PresentToAll />
          </Tooltip>
        );
      case "started":
        return (
          <Tooltip title="virtual presentation in progress">
            <Slideshow />
          </Tooltip>
        );
      case "completed":
        return (
          <Tooltip title="virtual presentation completed">
            <CheckIconOutlined sx={{ color: "green" }} />
          </Tooltip>
        );
      default:
        return <></>;
    }
  };

  const displayHasDrivingLicense = (applicantId: string) => {
    if (!applicantId) return <></>;
    const applicant = applicants[applicantId];
    if (applicant && applicant.has_driving_license) {
      return <CheckIcon sx={{ color: "green" }} />;
    } else {
      return <></>;
    }
  };

  /* ------------ Search functions -------------- */
  function descendingComparator(
    a: [string, VisaApplication],
    b: [string, VisaApplication],
    orderBy: string,
  ): number {
    const application1 = a[1];
    const application2 = b[1];

    if (!orderBy) {
      return 0;
    }

    // console.log("a1: ");
    // console.log(application1);

    // console.log("a2: ");
    // console.log(application2);

    // console.log("orderBy: " + orderBy);

    if (application1 && application2) {
      let res: number = 0;
      switch (orderBy) {
        case "has_driving_license":
          const d1 =
            application1.applicantId &&
            applicants[application1.applicantId]?.has_driving_license
              ? 1
              : 0;
          const d2 =
            application2.applicantId &&
            applicants[application2.applicantId]?.has_driving_license
              ? 1
              : 0;

          if (d1 === d2) {
            res = 0;
          } else if (d1 > d2) {
            res = -1;
          } else res = 1;

          return res;
        case "dates":
          res = application1.start_date && !application2.start_date ? 1 : 0;
          if (res === 0) {
            res = !application1.start_date && application2.start_date ? -1 : 0;
          }
          if (res === 0 && application1.start_date && application2.start_date) {
            res =
              new Date(application2.start_date).getTime() -
              new Date(application1.start_date).getTime();
          }

          // end date
          if (res === 0) {
            res = application1.end_date && !application2.end_date ? 1 : 0;
          }

          if (res === 0) {
            res = !application1.end_date && application2.end_date ? -1 : 0;
          }

          if (res === 0 && application1.end_date && application2.end_date) {
            res =
              new Date(application2.end_date).getTime() -
              new Date(application1.end_date).getTime();
          }
          return res;

        case "name":
          const name1 = applicantFullName(applicants[application1.applicantId]);
          const name2 = applicantFullName(applicants[application2.applicantId]);

          res = name2.localeCompare(name1);
          return res;

        case "workOrderId":
          if (application1.workOrderId && application2.workOrderId) {
            const e1 = orders[application1.workOrderId]?.name || "";
            const e2 = orders[application2.workOrderId]?.name || "";

            res = e2.localeCompare(e1);
          } else if (application1.workOrderId && !application2.workOrderId) {
            res = 1;
          } else if (!application1.workOrderId && application2.workOrderId) {
            res = -1;
          } else res = 0;
          return res;

        case "sponsorId":
          if (application1.sponsorId && application2.sponsorId) {
            const e1 = sponsors[application1.sponsorId].name;
            const e2 = sponsors[application2.sponsorId].name;
            res = e2.localeCompare(e1);
            return res;
          } else if (application1.sponsorId && !application2.sponsorId) {
            return -1;
          } else if (!application1.sponsorId && application2.sponsorId) {
            return 1;
          } else return 0;

        case "finished":
          res =
            application1.dataForm_expiry_date &&
            !application2.dataForm_expiry_date
              ? 1
              : 0;
          if (res === 0) {
            res =
              !application1.dataForm_expiry_date &&
              application2.dataForm_expiry_date
                ? -1
                : 0;
          }
          if (
            res === 0 &&
            application1.dataForm_expiry_date &&
            application2.dataForm_expiry_date
          ) {
            res =
              new Date(application2.dataForm_expiry_date).getTime() -
              new Date(application1.dataForm_expiry_date).getTime();
          }
          return res;

        case "privacy_policy_accepted":
          if (
            application1.privacy_policy_accepted &&
            !application2.privacy_policy_accepted
          ) {
            res = 1;
          } else if (
            !application1.privacy_policy_accepted &&
            application2.privacy_policy_accepted
          ) {
            res = -1;
          } else res = 0;

          return res;

        case "status":
          return application2.status.localeCompare(application1.status);
        default:
          break;
      }
    } else {
      if (application1 && !application2) {
        return -1;
      } else if (!application1 && application2) {
        return 1;
      }
    }
    return 0;
  }

  const filteredApplications = useMemo(
    () =>
      Object.entries(applications).filter(([applicationId, application]) => {
        const applicant = applicants[application.applicantId];
        try {
          const sval = searchValue.toLocaleLowerCase();
          const order = application.workOrderId
            ? orders[application.workOrderId]
            : null;

          const agent = application.agentId
            ? agents[application.agentId]
            : null;

          return (
            searchValue.length < 3 ||
            applicantFullName(applicant).toLocaleLowerCase().includes(sval) ||
            (applicant &&
              applicant.email?.toLocaleLowerCase().includes(sval)) ||
            (applicant &&
              displayStatusLabel(application.status)
                .toLocaleLowerCase()
                .includes(sval)) ||
            (application.sponsorId &&
              sponsors[application.sponsorId].name
                .toLowerCase()
                .includes(sval)) ||
            (order &&
              order.name &&
              (order.name.toLowerCase().includes(sval) ||
                order.start_date?.includes(sval) ||
                order.end_date?.includes(sval))) ||
            (agent && agent.company.toLowerCase().includes(sval)) ||
            (application.cos && application.cos.toLowerCase().includes(sval)) ||
            (application.gwf && application.gwf.toLowerCase().includes(sval)) ||
            (applicant &&
              applicant.nationality?.label?.toLowerCase().includes(sval))
            //     ||
            // //
            // (application.employerId &&
            //   employers[application.employerId].name.toLowerCase().includes(sval)) ||
            // application.start_date?.includes(sval) ||
            // application.end_date?.includes(sval)
          );
        } catch (err) {
          if (err instanceof Error) {
            console.error("filter exception:" + err.message);
          } else {
            console.error("filter exception:", err);
          }
          return false;
        }
      }),
    [searchValue, applications, sponsors, orders, applicants],
  );

  const visibleRows = useMemo(
    () =>
      filteredApplications
        .slice()
        .sort(
          getComparator(
            order,
            orderBy as keyof VisaApplication,
            descendingComparator,
          ),
        ),
    [order, orderBy, filteredApplications],
  );

  const handleToggleSortWorkOrders = () => {
    setSortWorkOrdersBy((prev) => (prev === "name" ? "date" : "name"));
  };

  const sortAndFilterOrders = (
    fOrders: WorkOrdersDict,
    showOpen: boolean,
    season: string,
  ) => {
    const filteredOrders = Object.entries(fOrders)
      .filter(([orderId, order]) => {
        if (season && order.seasonId !== season) {
          return false;
        }

        return !showOpen || order.status !== "closed";
      })
      .sort(([idA, orderA], [idB, orderB]) => {
        if (
          sortWorkOrdersBy === "date" &&
          orderA.start_date &&
          orderB.start_date
        ) {
          // Sort by start date if both have dates
          const dateA = new Date(orderA.start_date).getTime();
          const dateB = new Date(orderB.start_date).getTime();

          // If dates are the same, sort by name
          if (dateA === dateB) {
            return orderA.name.localeCompare(orderB.name);
          }

          return dateA - dateB;
        }
        // Default sort by name
        return orderA.name.localeCompare(orderB.name);
      });

    return filteredOrders;
  };

  const sortedWorkOrders = useMemo(
    () => sortAndFilterOrders(orders, showOpenOrders, selectedSeason),
    [orders, showOpenOrders, selectedSeason, sortWorkOrdersBy],
  );

  const sortedSeasons = useMemo(
    () =>
      Object.entries(seasons).sort(([, a], [, b]) =>
        a.name.localeCompare(b.name),
      ),
    [seasons],
  );

  const handleSearchValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchValue(event.target.value);
  };

  const getFooterText = () => {
    let text = "";
    let orderDemand = 0;
    const vrl = visibleRows.length;

    if (
      selectedOrder &&
      selectedOrder !== "all_orders" &&
      selectedOrder !== "no_order"
    ) {
      orderDemand = orders[selectedOrder].demand;
      text = " / " + orderDemand;
    }

    let color = "black";
    if (orderDemand > 0) {
      if (vrl < orderDemand) {
        color = "red";
      } else {
        color = "green";
      }
    }
    return (
      <Typography variant="body2" color={color} component="div">
        {vrl + text + " Applications"}
      </Typography>
    );
  };

  const handleCloseDialog = () => {
    setBatchUpdateStage("idle");
  };

  return (
    <TitledPage title="Applications">
      <>
        <Box
          id="applications-page"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: "100vh",
          }}
        >
          <Box display={"flex"} sx={{ gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              sx={{ marginTop: "8px", marginBottom: "4px" }}
              onClick={() => handleAddNewApplication("")}
            >
              Add New Application
            </Button>

            <ApplicationDialog
              open={open}
              onClose={handleClose}
              onSave={handleSaveApplication}
              application={editAppl}
              applicants={applicants}
            />
            <ApplicationVirtualPresentationDialog
              open={openVPDilog}
              onClose={handleCloseVPDialog}
              onSave={handleSaveApplicationVP}
              onSendLink={() =>
                handleSendSelectedInvition(
                  selectedApplication?.id || "",
                  "virtual_presentations",
                )
              }
              application={editAppl}
            />
            {/* <Tooltip
              title={
                selected.length === 0
                  ? "Convert all approved candidates to applicants"
                  : "Convert selected approved candidates to applicants"
              }
              title="Send an invitation mail to the selected applicant"
            >
              <IconButton
                color="primary"
                edge={"end"}
                sx={{ marginTop: "8px", marginBottom: "4px" }}
                onClick={handleSendInvitations}
              >
                <AssignmentInd />
              </IconButton>
            </Tooltip> */}

            {/* Application Status */}
            {/* <Menu
              id="status-menu"
              anchorEl={statusAnchorEl}
              keepMounted
              open={Boolean(statusAnchorEl)}
              onClose={handleCloseStatus}
            >
              {APPLICATION_STATUS.map((status) => (
                <Tooltip title={status.description}>
                  <MenuItem key={status.key} onClick={() => handleStatusChange(status.key)}>
                    {status.label}
                  </MenuItem>
                </Tooltip>
              ))}
            </Menu> */}

            <ApplicationStatusDialog
              open={openStatus}
              onClose={handleCloseStatus}
              onSave={handleSaveApplication}
              application={editAppl}
            />

            {/* Dataform Link */}
            <SimpleLinkDialog
              label={currentLink}
              open={openAlert}
              showQRCode={false}
              showSendLink={true}
              onSendLink={() =>
                handleSendSelectedInvition(
                  selectedApplication?.id || "",
                  "data_form",
                )
              }
              title={
                "Applicant data survey for " +
                getApplicantFullName(
                  selectedApplication?.application?.applicantId,
                )
              }
              onClose={handleAlertClose}
            />
            <Tooltip title="Export applications to CSV">
              <span>
                <IconButton
                  color="primary"
                  edge={"end"}
                  sx={{ marginTop: "8px", marginBottom: "4px" }}
                  onClick={handleExport}
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Batch Updates */}
            <ApplicationsBatchUpdateDialog
              open={openBatchUpdate}
              onClose={() => setOpenBatchUpdate(false)}
              onBatchUpdate={onBatchUpdate}
              onSendDataFormLink={handleSendInvitations}
              initialValues={batchUpdateValues}
              numberOfSelected={selected.length}
              agents={agents}
              workOrders={orders}
              seasons={seasons}
              virtualPresentations={presentations}
            />
            <Tooltip title="Batch edit applications">
              <span>
                <IconButton
                  color="primary"
                  edge={"end"}
                  disabled={selected.length === 0}
                  sx={{ marginTop: "8px", marginBottom: "4px" }}
                  onClick={handleBatchUpdate}
                >
                  <EditNoteIcon />
                </IconButton>
              </span>
            </Tooltip>
            {hasRole(currentUser?.appUser, ["admin"]) && (
              <>
                <DocumentHistoryDialog
                  open={openHistory}
                  onClose={() => setOpenHistory(false)}
                  documentId={historyDoc}
                />
                <Tooltip title="History">
                  <span>
                    <IconButton
                      color="primary"
                      edge={"end"}
                      sx={{ marginTop: "8px", marginBottom: "4px" }}
                      disabled={!selectedApplication}
                      onClick={() =>
                        handleShowHistory(selectedApplication?.id || "")
                      }
                    >
                      <InfoIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}

            {/* <Button
              variant="outlined"
              color="primary"
              sx={{ marginTop: "8px", marginBottom: "4px" }}
              onClick={handleExport}
            >
              Export applications
            </Button> */}
            {/* <Button variant="outlined" color="primary" onClick={handleCallFunction}>
              Test functions
            </Button> */}

            {/* ----------------- Season ---------------- */}
            <TextField
              name={"seasonId"}
              id={"seasonId"}
              label={"Season"}
              select
              onChange={onSeasonInputComboChange}
              value={selectedSeason}
              margin={"dense"}
              size="small"
              sx={{ width: "150px" }}
            >
              <MenuItem value="no_season" key="no_season">
                <Typography fontStyle={"italic"}>no season</Typography>
              </MenuItem>
              {sortedSeasons.map(([seasonId, season]) => {
                return (
                  <MenuItem value={seasonId} key={seasonId}>
                    {season.name}
                  </MenuItem>
                );
              })}
            </TextField>

            {/* ----------------- Work Orders ---------------- */}
            <WorkOrderSearchBox
              selectedOrder={selectedOrder}
              showOpenOrders={showOpenOrders}
              sortWorkOrdersBy={sortWorkOrdersBy}
              sortedWorkOrders={sortedWorkOrders}
              onInputComboChange={onInputComboChange}
              onToggleShowOpenOrders={handleToggleShowopenOrders}
              onToggleSortWorkOrders={handleToggleSortWorkOrders}
              getWorkOrderStatusChip={getWorkOrderStatusChip}
            />

            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={searchValue}
              onChange={handleSearchValueChange}
              sx={{ minWidth: "250px", marginTop: "8px", marginBottom: "4px" }}
            />
          </Box>

          <Box
            id="applications-container"
            sx={{
              display: "flex",
              flexDirection: "column",
              marginTop: "1rem",
              height: "calc(100vh - 250px)",
            }}
          >
            <Box
              id="applications-table-container"
              sx={{ marginBottom: "24px", minHeight: "400px", height: "66%" }}
            >
              <TableVirtuoso
                style={{ height: "100%" }}
                data={visibleRows}
                context={{
                  selectedId: selectedApplication?.id || "",
                  handleSelect: handleSelectApplication,
                }}
                components={VirtuosoTableComponents}
                fixedHeaderContent={() => (
                  <EnhancedTableHeadContents
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={visibleRows.length}
                    selectable={true}
                    headCells={headCells}
                  />
                )}
                fixedFooterContent={() => (
                  <TableRow>
                    <TableCell align="right" colSpan={headCells.length + 1}>
                      {getFooterText()}
                    </TableCell>
                  </TableRow>
                )}
                itemContent={(index, [applicationId, application]) => {
                  return (
                    <ApplicationRow
                      key={applicationId}
                      index={index}
                      applicationId={applicationId}
                      application={application}
                      handleStatusChange={handleStatusChange}
                      handleEditApplication={handleEditApplication}
                      handleSendLink={handleSendLink}
                      handleAssignVirtualPresentation={
                        handleAssignVirtualPresentation
                      }
                      handleDelete={handleDelete}
                      isSelected={isSelected}
                      getApplicantFullName={getApplicantFullName}
                      getOrderName={getOrderName}
                      displayVPStatusIcon={displayVPStatusIcon}
                      displayStatusDescription={displayStatusDescription}
                      displayStatusLabel={displayStatusLabel}
                      displayHasDrivingLicense={displayHasDrivingLicense}
                      passportValidityWarning={getApplicationPassportValidityWarningText(
                        application,
                        applicationId,
                      )}
                      handleCheckRow={handleCheckRow}
                    />
                  );
                }}
              />
            </Box>
            {selectedApplication && (
              <Box
                id="application-details-container"
                sx={{ display: "flex", flexGrow: 1 }}
              >
                <ApplicationDetails
                  application={selectedApplication.application}
                  applicant={{
                    id: selectedApplication.application.applicantId,
                    applicant:
                      applicants[selectedApplication.application.applicantId],
                  }}
                  sponsor={sponsors[selectedApplication.application.sponsorId]}
                  agent={
                    selectedApplication.application.agentId
                      ? agents[selectedApplication.application.agentId]
                      : undefined
                  }
                  presentation={
                    selectedApplication.application.virtualPresentation
                      ?.virtualPresentationId
                      ? presentations[
                          selectedApplication.application.virtualPresentation
                            .virtualPresentationId
                        ]
                      : undefined
                  }
                  isGeneratingEmailExtensionCode={
                    generatingApplicantCodeId ===
                    selectedApplication.application.applicantId
                  }
                  onGenerateEmailExtensionCode={handleGenerateApplicantCode}
                  onCopyEmailExtensionCode={
                    handleCopyApplicantEmailExtensionCode
                  }
                />
              </Box>
            )}
          </Box>
        </Box>

        <Dialog
          open={passportWarnings.length > 0}
          onClose={() => setPassportWarnings([])}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Passport validity warning</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {passportWarnings.length === 1
                ? "This applicant's passport expires less than 6 months after the assigned work order ends."
                : "These applicants' passports expire less than 6 months after their assigned work orders end."}
            </Typography>
            {passportWarnings.map((warning, index) => (
              <Box key={`${warning.applicationId}-${index}`} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {getPassportValidityWarningText(warning)}
                </Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPassportWarnings([])} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Batch Update Progress Dialog */}
        <Dialog open={batchUpdateStage !== "idle"} fullWidth maxWidth="sm">
          <DialogContent>
            <Box sx={{ width: "100%", textAlign: "center", py: 3 }}>
              {batchUpdateStage === "updating" && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Updating Applications...
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Processing record {progress.current} of {progress.total}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={
                      progress.total > 0
                        ? (progress.current / progress.total) * 100
                        : 0
                    }
                    sx={{ mt: 2 }}
                  />
                </>
              )}
              {batchUpdateStage === "fetching" && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Fetching updated applications...
                  </Typography>
                  {/* Optional: Add an indeterminate progress bar or spinner here */}
                  <LinearProgress sx={{ mt: 2 }} />
                </>
              )}
              {batchUpdateStage === "complete" && (
                <>
                  <Typography variant="h6" gutterBottom color="success.main">
                    Update Complete
                  </Typography>
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="body1" gutterBottom>
                      Total records processed: {progress.total}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Applications successfully updated: {progress.updated}
                    </Typography>
                    {progress.failed > 0 && (
                      <Typography variant="body1" gutterBottom color="error">
                        Applications failed to update: {progress.failed}
                      </Typography>
                    )}
                    {/* Add more details if needed, e.g., failed updates */}
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleCloseDialog}
                    sx={{ mt: 2 }}
                  >
                    Close
                  </Button>
                </>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </>
    </TitledPage>
  );
};

export default ApplicationsPage;
